import React, { useEffect, useRef, useState, type FC } from 'react'
import {
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
} from '@moonpig/launchpad-components'
import { styled, breakpoint } from '@moonpig/launchpad-utils'
import { system as s } from '@moonpig/launchpad-system'
import { useLocaleText } from '../../../locale/moreLikeThis.locale'
import { createViewItemListGAEvent } from '../../../analytics'
import type { ProductInfoProduct, SearchCriteria } from '../../../types'
import { MoreLikeThisTabPanel } from './MoreLikeThisTabPanel'
import {
  MORE_LIKE_THIS_DEFAULT_TAB,
  moreLikeThisTabsConfig,
  type MoreLikeThisTabLabelKey,
  type MoreLikeThisTabValue,
} from './moreLikeThisTabsConfig'
import { useMoreLikeThisQueries } from '../hooks/useMoreLikeThisQueries'
import { MoreLikeThisTabsSkeleton } from './MoreLikeThisTabsSkeleton'
import {
  createGenericSelectContentEvent,
  trackGAEvent,
} from '@moonpig/web-core-analytics'

const StyledTabs = styled(Tabs)`
  [role='tablist'] {
    flex-wrap: nowrap;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
    scrollbar-width: none;

    ${s({
      mb: 6,
      py: 0,
      px: 0,
    })}

    ${breakpoint('md')} {
      ${s({
        px: 8,
      })}
    }

    &::-webkit-scrollbar {
      display: none;
    }
  }

  [role='tab'] {
    flex: 0 0 auto;
    white-space: nowrap;
  }

  [role='tabpanel'] {
    ${s({
      px: { xs: 6, md: 8 },
    })}
  }
`

type Props = {
  searchCriteria: SearchCriteria
  baseProduct: ProductInfoProduct
}

export const MoreLikeThisTabs: FC<Props> = ({
  searchCriteria,
  baseProduct,
}) => {
  const localiseText = useLocaleText()
  const { productsBySection, loading } = useMoreLikeThisQueries(
    searchCriteria,
    baseProduct,
  )
  const [activeTab, setActiveTab] = useState<MoreLikeThisTabValue | null>(null)
  const lastTrackedTabRef = useRef<MoreLikeThisTabValue | null>(null)
  const availableTabs = moreLikeThisTabsConfig.filter(
    ({ value }) => productsBySection[value].length > 0,
  )
  const selectedTab =
    (activeTab &&
      availableTabs.find(({ value }) => value === activeTab)?.value) ??
    availableTabs.find(({ value }) => value === MORE_LIKE_THIS_DEFAULT_TAB)
      ?.value ??
    availableTabs[0]?.value ??
    MORE_LIKE_THIS_DEFAULT_TAB

  const onTabChange = (value: string) => {
    setActiveTab(value as MoreLikeThisTabValue)
    trackGAEvent(
      createGenericSelectContentEvent({
        eventCategory: 'product details',
        eventAction: 'tab click',
        eventLabel: localiseText(
          availableTabs.find(({ value: tabValue }) => tabValue === value)
            ?.labelKey as MoreLikeThisTabLabelKey,
        ),
      }),
    )
  }

  useEffect(() => {
    if (loading) {
      return
    }

    if (lastTrackedTabRef.current === selectedTab) {
      return
    }

    const tabConfig = availableTabs.find(({ value }) => value === selectedTab)

    if (!tabConfig) {
      return
    }

    lastTrackedTabRef.current = selectedTab

    trackGAEvent(
      createViewItemListGAEvent({
        label: `product details | ${localiseText(tabConfig.labelKey)} | tabbed`,
        products: productsBySection[selectedTab],
      }),
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTab, loading])

  if (loading) {
    return (
      <MoreLikeThisTabsSkeleton numberOfTabs={moreLikeThisTabsConfig.length} />
    )
  }

  if (availableTabs.length === 0) {
    return null
  }

  return (
    <StyledTabs value={selectedTab} onValueChange={onTabChange}>
      <TabList>
        {availableTabs.map(({ value, labelKey }) => (
          <Tab key={value} value={value}>
            {localiseText(labelKey)}
          </Tab>
        ))}
      </TabList>
      <TabPanels>
        {availableTabs.map(({ value, labelKey }, index) => (
          <TabPanel key={value} value={value}>
            <MoreLikeThisTabPanel
              products={productsBySection[value]}
              listName={localiseText(labelKey)}
              listPosition={`${index + 1}/${availableTabs.length}`}
            />
          </TabPanel>
        ))}
      </TabPanels>
    </StyledTabs>
  )
}
